import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { app, db } from '../firebase/config';

export type Identity = {
  /** The uid every server-side authorisation check compares against. */
  userId: string;
  /** What other people see in the queue and the discovery list. */
  displayName: string;
};

/** Null when signed out — callers must handle it rather than inventing an id. */
export function currentIdentity(): Identity | null {
  return toIdentity(getAuth(app).currentUser);
}

/*
 * updateProfile changes the Auth user in place and fires no auth event, so a
 * renamed person kept their old name on every screen until the next sign-in.
 * A rename calls notifyIdentityChanged() and every hook re-reads.
 */
const identityListeners = new Set<() => void>();
export function notifyIdentityChanged(): void {
  identityListeners.forEach((listener) => listener());
}

function toIdentity(user: User | null): Identity | null {
  if (!user) return null;
  return { userId: user.uid, displayName: user.displayName || '' };
}

/**
 * C1 — every user's display name used to be empty in practice: nothing in
 * this codebase ever calls updateProfile, verifyOtpAndSignIn.js creates the
 * Auth user with { phoneNumber } only, and the Auth-collision recovery path
 * (the same function, the auth/phone-number-already-exists branch) can land
 * on an Auth profile whose displayName was never (re)synced even though
 * Firestore's users/{uid}.displayName holds a real one. Returning '' from
 * the Auth profile alone is what turned into a permission-denied nobody
 * could act on — validMuqriName and the queue's own name check both require
 * a non-empty name, and a client has no way to tell "the write was refused
 * because the name is empty" apart from any other permission-denied.
 *
 * Re-renders every screen on sign-in and sign-out without any of them
 * changing, and falls back to the Firestore document ONLY while the Auth
 * profile's own name is empty — once it has one, the fallback listener is
 * torn down rather than left running forever.
 */
export function useIdentityState(): {
  identity: Identity | null;
  restoring: boolean;
  resolvingName: boolean;
} {
  const [identity, setIdentity] = useState<Identity | null>(() => currentIdentity());
  /**
   * Firebase restores the signed-in user from storage asynchronously, so a
   * cold load reports null for a tick before it turns out somebody IS signed
   * in. Callers that read that null as 'signed out' throw the user off the
   * page they asked for on every refresh — that is what sent /tracks to /home
   * in production. `restoring` is the difference between 'nobody is signed
   * in' and 'nobody has said yet'.
   */
  const [restoring, setRestoring] = useState(() => currentIdentity() === null);
  /*
   * True while the name is still being LOOKED UP, as opposed to known to be
   * absent. Every account created by verifyOtpAndSignIn has an empty Auth
   * displayName, so between auth answering and the fallback below arriving,
   * `displayName` is '' for people who have had a name all along. A caller
   * that reads that as "no name" sends them to the profile step — observed
   * live, a parent whose name was in Firestore landed on أكمل ملفك and stayed
   * there, because nothing sends you back off that page.
   */
  const [answered, setAnswered] = useState(false);
  useEffect(() => onAuthStateChanged(getAuth(app), (u) => {
    setIdentity(toIdentity(u));
    setRestoring(false);
  }), []);
  useEffect(() => {
    const reread = () => setIdentity(currentIdentity());
    identityListeners.add(reread);
    return () => { identityListeners.delete(reread); };
  }, []);

  useEffect(() => {
    if (!identity || identity.displayName) return undefined;
    const uid = identity.userId;
    return onSnapshot(doc(db, 'users', uid), (snap) => {
      // Answered either way: a document with no name is the case where the
      // profile step IS the right place to be.
      setAnswered(true);
      const fallback = snap.data()?.displayName;
      if (typeof fallback !== 'string' || !fallback) return;
      // Guards against a stale write landing after a sign-out/sign-in swap,
      // or after the Auth profile itself picked up a name in the meantime —
      // never overwrite a real name with a fallback answering a question
      // that no longer applies.
      setIdentity((prev) => (
        prev && prev.userId === uid && !prev.displayName ? { ...prev, displayName: fallback } : prev
      ));
    });
    // Re-subscribes only when the uid changes, or when the fallback itself
    // just supplied a name (which flips the guard above and tears the
    // listener down) — not on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity?.userId, identity?.displayName]);

  return {
    identity,
    restoring,
    // Only while there is somebody signed in, with no name yet, and the
    // lookup has not come back.
    resolvingName: !!identity && !identity.displayName && !answered,
  };
}

/** The identity alone, for the callers that have nothing to wait on. */
export function useIdentity(): Identity | null {
  return useIdentityState().identity;
}
